import React, { useState, useEffect, useRef, forwardRef } from "react";
import MeaCreateModal from "../../../../components/MeaCreateModal";
import { InputTextarea } from "primereact/inputtextarea";
import useAuthContext from "../../../../hooks/useAuthContext";
import { InputNumber } from "primereact/inputnumber";
import paymentsAPI from "../../../../services/payments";
import trainingsAPI from "../../../../services/trainings";
import uiDataAPI from "../../../../services/ui_data";
import { SelectButton } from "primereact/selectbutton";
import { Calendar } from "primereact/calendar";
import { classNames } from "primereact/utils";
import { Message } from "primereact/message";
import { Skeleton } from "primereact/skeleton";

const CreateContractPaymentModal = forwardRef((props, ref) => {
    //
    const schoolId = props?.schoolId;
    const trainingId = props?.trainingId;
    const accessToken = "";
    //
    const [contractId, setContractId] = useState(props.contractId || "");
    const [method, setMethod] = useState(() => {
        // Initialize from localStorage or default to "CASH"
        return localStorage.getItem('lastPaymentMethod') || "CASH";
    });
    const [amount, setAmount] = useState("0");
    const [status, setStatus] = useState(() => {
        // Initialize from localStorage or default to "PAID"
        return localStorage.getItem('lastPaymentStatus') || "PAID";
    });
    const [paymentDate, setPaymentDate] = useState(new Date());
    const [paymentReceivedDate, setPaymentReceivedDate] = useState(new Date());
    const [details, setDetails] = useState("");
    const [paymentSettings, setPaymentSettings] = useState(null);
    const [loadingPaymentSettings, setLoadingPaymentSettings] = useState(true);
    //
    const modalRef = useRef(null);
    const amountInputRef = useRef(null);
    //
    const UiDataPaymentMethodTypesListQuery = uiDataAPI.UiDataPaymentMethodTypesList(
        accessToken
    );
    const UiDataPaymentStatusTypesListQuery = uiDataAPI.UiDataPaymentStatusTypesList(
        accessToken
    );
    const paymentMethods = UiDataPaymentMethodTypesListQuery?.data?.data || [];
    const paymentStatusOptions = UiDataPaymentStatusTypesListQuery?.data?.data || [];
    const selectablePaymentStatusOptions = paymentStatusOptions.filter((option) =>
        option?.id === "PAID" || option?.id === "PENDING"
    );

    useEffect(() => {
        let isMounted = true;
        const loadPaymentSettings = async () => {
            setLoadingPaymentSettings(true);
            try {
                const result = await trainingsAPI.GetPaymentSettings(schoolId, trainingId, accessToken);
                if (!isMounted) {
                    return;
                }
                setPaymentSettings(result?.data || null);
            } catch {
                if (isMounted) {
                    setPaymentSettings(null);
                }
            } finally {
                if (isMounted) {
                    setLoadingPaymentSettings(false);
                }
            }
        };

        if (schoolId && trainingId) {
            loadPaymentSettings();
        }

        return () => {
            isMounted = false;
        };
    }, [schoolId, trainingId, accessToken]);

    const isMethodEnabled = (methodId) => {
        if (!paymentSettings) {
            return false;
        }

        const methodMapping = {
            CASH: paymentSettings.cache_enabled,
            CB: paymentSettings.cb_enabled,
            CHEQUE: paymentSettings.cheque_enabled,
            HELLOASSO: paymentSettings.helloasso_enabled,
            OTHER: paymentSettings.other_enabled,
        };

        return Boolean(methodMapping[methodId]);
    };

    const enabledPaymentMethods = paymentMethods.filter((item) => isMethodEnabled(item?.id));
    const isLoadingPaymentMethods = loadingPaymentSettings || UiDataPaymentMethodTypesListQuery?.isLoading;
    const hasPaymentMethods = enabledPaymentMethods.length > 0;

    const applyDefaultStatusForMethod = (methodId) => {
        if (methodId === "CASH" || methodId === "CB" || methodId === "HELLOASSO") {
            setStatus("PAID");
        } else if (methodId === "CHEQUE" || methodId === "OTHER") {
            setStatus("PENDING");
        }
    };

    useEffect(() => {
        if (!hasPaymentMethods) {
            return;
        }
        const methodExists = enabledPaymentMethods.some((item) => item?.id === method);
        if (!methodExists) {
            const fallbackMethod = enabledPaymentMethods[0]?.id;
            if (fallbackMethod) {
                setMethod(fallbackMethod);
                applyDefaultStatusForMethod(fallbackMethod);
            }
        }
    }, [hasPaymentMethods, enabledPaymentMethods, method]);

    // Update localStorage when payment method changes
    useEffect(() => {
        if (method) {
            localStorage.setItem('lastPaymentMethod', method);
        }
    }, [method]);

    // Update localStorage when payment status changes
    useEffect(() => {
        if (status) {
            localStorage.setItem('lastPaymentStatus', status);
        }
    }, [status]);

    useEffect(() => {
        if (method === "CASH" && status !== "PAID") {
            setStatus("PAID");
        }
    }, [method, status]);

    useEffect(() => {
        if (method === "CB" && status !== "PAID") {
            setStatus("PAID");
        }
    }, [method, status]);

    useEffect(() => {
        if (method === "CHEQUE" && status !== "PENDING") {
            setStatus("PENDING");
        }
    }, [method, status]);

    useEffect(() => {
        if (method === "CASH" || method === "CB") {
            setPaymentDate(paymentReceivedDate || new Date());
        }
    }, [method, paymentReceivedDate]);

    const focusAmountInput = () => {
        if (method === "HELLOASSO") {
            return;
        }
        setTimeout(() => {
            const inputEl = amountInputRef.current || document.getElementById("amount");
            if (inputEl && typeof inputEl.focus === "function") {
                inputEl.focus();
            }
        }, 0);
    };

    useEffect(() => {
        focusAmountInput();
    }, [method]);

    return (
        <>
            <MeaCreateModal
                ref={(el) => {
                    modalRef.current = el;
                    if (ref) {
                        if (typeof ref === 'function') {
                            ref(el);
                        } else {
                            ref.current = el;
                        }
                    }
                }}
                title="Ajouter un paiement"
                serviceAPI={paymentsAPI}
                schoolId={schoolId}
                trainingId={trainingId}
                data={{
                    contract_id: contractId,
                    amount: amount,
                    method: method,
                    status: status,
                    payment_date: (() => {
                        // Set paymentDate to mid-day of the chosen date
                        const currentDate = paymentDate || new Date();
                        const middayDate = new Date(currentDate);
                        middayDate.setHours(12, 0, 0, 0);
                        return middayDate;
                    })(),
                    payment_received_date: (() => {
                        // Set paymentReceivedDate to mid-day of the chosen date
                        const currentDate = paymentReceivedDate || new Date();
                        const middayDate = new Date(currentDate);
                        middayDate.setHours(12, 0, 0, 0);
                        return middayDate;
                    })(),
                    payment_refund_date: null,
                    details: details,
                }}
                onSubmitted={() => {
                    setAmount("0");
                    // Don't reset method - keep the last selected payment method
                    //setStatus("");
                    setDetails("");
                }}
                onShow={focusAmountInput}
            >
                <label htmlFor="method" className="block text-900 font-medium mb-2">
                    Méthode de paiement
                </label>
                {isLoadingPaymentMethods ? (
                    <div className="w-full mt-2 mb-3">
                        <Skeleton height="3rem" className="mb-2" />
                        <Skeleton height="3rem" width="75%" />
                    </div>
                ) : (
                    <SelectButton
                        value={enabledPaymentMethods?.find(
                            (obj) => obj.id === method
                        )}
                        options={enabledPaymentMethods}
                        disabled={!hasPaymentMethods}
                        onChange={(e) => {
                            setMethod(e.value?.id);
                            applyDefaultStatusForMethod(e.value?.id);
                        }}
                        optionLabel="name"
                        itemTemplate={(option) => (
                            <div className="flex align-items-center">
                                <i
                                    className={classNames('text-900', {
                                        'pi pi-euro': option.id === 'CASH',
                                        'pi pi-credit-card': option.id === "CB",
                                        'pi pi-wallet': option.id === "CHEQUE",
                                        'pi pi-globe': option.id === "HELLOASSO",
                                        'pi pi-align-justify': option.id === "OTHER"
                                    })}
                                    style={{ fontSize: "1.5rem" }}
                                ></i>
                                <span className="pl-2">{option.name}</span>
                            </div>
                        )}
                    />
                )}

                {!isLoadingPaymentMethods && !hasPaymentMethods && (
                    <Message
                        severity="info"
                        text="Aucune méthode de paiement activée pour cette formation. Veuillez d'abord configurer les méthodes de paiement."
                        className="w-full mt-3"
                    />
                )}

                {!isLoadingPaymentMethods && hasPaymentMethods && method === "HELLOASSO" ? (
                    <Message severity="info" text="Paiement uniquement via l'Espace Famille." className="w-full mt-3" />
                ) : !isLoadingPaymentMethods && hasPaymentMethods ? (
                    <>
                <label
                    htmlFor="amount"
                    className="block text-900 font-medium mb-2"
                >
                    Montant
                </label>
                <InputNumber
                    id="amount"
                    inputRef={amountInputRef}
                    value={amount}
                    showButtons
                    min={0}
                    onValueChange={(e) => setAmount(e.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            // Update amount with current input value before submitting
                            const currentValue = parseFloat(e.target.value) || 0;
                            if (currentValue !== amount) {
                                setAmount(currentValue);
                                // Use setTimeout to ensure state is updated before submit
                                setTimeout(() => {
                                    if (modalRef.current && modalRef.current.submit) {
                                        modalRef.current.submit();
                                    }
                                }, 0);
                            } else {
                                if (modalRef.current && modalRef.current.submit) {
                                    modalRef.current.submit();
                                }
                            }
                        }
                    }}
                    rows={3}
                    className="w-full mb-3"
                    autoFocus
                />
                {method !== "CASH" && method !== "CB" && method !== "CHEQUE" && (
                    <>
                <label htmlFor="name" className="block text-900 font-medium mb-2">
                    Status
                </label>
                <SelectButton
                    value={UiDataPaymentStatusTypesListQuery?.data?.data?.find(
                        (obj) => obj.id === status
                    )}
                    options={selectablePaymentStatusOptions}
                    onChange={(e) => setStatus(e.value?.id)}
                    optionLabel="name"
                    itemTemplate={(option) => (
                        <div className="flex align-items-center">
                            <i
                                className={classNames('text-900', {
                                    'pi pi-check-circle': option.id === 'PAID',
                                    'pi pi-hourglass': option.id === "PENDING",
                                    'pi pi-replay': option.id === "REFUNDED"
                                })}
                                style={{ fontSize: "1.5rem" }}
                            ></i>
                            <span className="pl-2">{option.name}</span>
                        </div>
                    )}
                />
                    </>
                )}

                <label
                    htmlFor="paymentReceivedDate"
                    className="block text-900 font-medium mb-2"
                >
                    Date de réception
                </label>
                <Calendar
                    id="paymentReceivedDate"
                    value={paymentReceivedDate}
                    onChange={(e) => setPaymentReceivedDate(e.value)}
                    locale="fr"
                    dateFormat="dd/mm/yy"
                    showIcon
                    icon="pi pi-calendar"
                    iconPos="right"
                    showOnFocus={false}
                    showButtonBar
                    hideOnDateTimeSelect
                    className="w-full mb-3"
                />

                {(method !== "CASH" && method !== "CB") ? (
                    <>
                <label
                    htmlFor="paymentDate"
                    className="block text-900 font-medium mb-2"
                >
                    Date d'encaissement prévue
                </label>
                <Calendar
                    id="paymentDate"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.value)}
                    locale="fr"
                    dateFormat="dd/mm/yy"
                    showIcon
                    icon="pi pi-calendar"
                    iconPos="right"
                    showOnFocus={false}
                    showButtonBar
                    hideOnDateTimeSelect
                    className="w-full mb-3"
                />
                    </>
                ) : null}

                <label
                    htmlFor="details"
                    className="block text-900 font-medium mb-2"
                >
                    Détails
                </label>
                <InputTextarea
                    id="details"
                    type="text"
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    rows={3}
                    className="w-full mb-3"
                />
                    </>
                ) : null}
            </MeaCreateModal >
        </>
    );
});

export default CreateContractPaymentModal;
